import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LocalStorage from 'utils/myLocalStorage'
import ModalViewDocument from 'components/Modal/modalViewDocument'
import { DOCUMENT_TYPES, DisplayModus } from 'constants/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp, faChevronDown, faEye, faTrash } from '@fortawesome/free-solid-svg-icons'
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons'
import tools from 'utils/helpers'
import './woLinkedDocs.css'
import { LinkedDoc, LinkedDocInput } from 'types/workorder/linkedDoc'
import ModalLinkedDoc from './modalLinkedDoc'
import { Lang } from 'context/LangContext'
import {
  Collapsible,
  Inline,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@airbus/components-react/components'
import URL_APIS from 'utils/api/urlApis'
import useMutation from 'utils/hooks/useMutation'
import ComponentsUNames from 'constants/componentsUNames'
import { IconButton } from '@airbus/components-react/components'
import ModalConfirm from 'components/Modal/ModalConfim'

import Fieldset from 'components/Fieldset/fieldset'
import urlApis from 'utils/api/urlApis'
import useQuery from 'utils/hooks/useQuery'
type Props = {
  lang: Lang
  oenumdoc: string | null
  oeType: string | undefined
  displayMode: DisplayModus
  expandAll: boolean
}

type SelectedLinkedDoc = LinkedDoc & {
  checked?: boolean
  open?: boolean
  loading?: boolean
  errorMsg?: string
  done?: boolean
  date?: Date
  resourceType?: string
}

const WoLinkedDocs = ({ lang, oenumdoc, oeType, displayMode, expandAll }: Props) => {
  const { t } = useTranslation()
  // Label for linked documents with no category
  const EMPTY_DOC_CATEGORY = t('9500193')

  const [isModalFormOpen, setIsModalFormOpen] = useState<boolean>(false)
  const [modalFormDisplayMode, setModalFormDisplayMode] = useState(DisplayModus.READ)
  const [selectedResource, setSelectedResource] = useState<LinkedDocInput>()

  const [data, setData] = useState<SelectedLinkedDoc[]>()
  const { fetchData: fetchLinkedDocs } = useQuery<LinkedDoc[]>(urlApis.URL_WORKORDER_LINKED_DOCS_GET)

  // Expand/collapse status of main component
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  // Data grouped by type: {<Type_1>: [{...}, {...}], <Type_2>: [{...}, {...}]}
  const [dataGroupedByType, setDataGroupedByType] = useState({})
  // Expand/collapse status of grouped-by type tables
  const [isExpandedGroupedByType, setIsExpandedGroupedByType] = useState({})

  const [documentReference, setDocumentReference] = useState<string>('')
  // Url of document to display in the modal
  const [urlViewDocument, setUrlViewDocument] = useState<string>('')
  // State of the modal showing the document in display
  const [modalViewDocumentOpened, setModalViewDocumentOpened] = useState<boolean>(false)

  const [linkedDocsToDelete, setLinkedDocsToDelete] = useState<SelectedLinkedDoc | null>(null)
  const { mutate: deleteLinkedDocs, isLoading } = useMutation(URL_APIS.URL_WORKORDER_LINKED_DOCS_DELETE)

  const canUpdate =
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_PERMISSIONS],
      LocalStorage.VALUE_TYPE.BOOLEAN,
      false,
    ) &&
    (oeType !== 'DOC' ||
      (oeType === 'DOC' &&
        LocalStorage.getOrDefault(
          [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_OE_SB_PERMISSIONS],
          LocalStorage.VALUE_TYPE.BOOLEAN,
          false,
        )))

  const canCreate = canUpdate

  const handleModalFormPostSubmit = () => {
    setIsModalFormOpen(false)
    setSelectedResource(undefined)

    loadData()
  }

  const handleDelete = async () => {
    if (!linkedDocsToDelete) return
    setLinkedDocsToDelete({ ...linkedDocsToDelete, loading: true, open: false })
    await deleteLinkedDocs({
      params: {
        linkType: linkedDocsToDelete.linkType,
        oenumdoc: linkedDocsToDelete.numdoc,
        codeType: linkedDocsToDelete.codeType,
        typeDocSb: linkedDocsToDelete.typeDocSb,
        protocol: linkedDocsToDelete.protocol,
        reference: linkedDocsToDelete.reference,
        revision: linkedDocsToDelete.revision,
        item: linkedDocsToDelete.item,
      },
      method: 'DELETE',
    })

    setLinkedDocsToDelete(null)
    loadData()
  }

  const handleCreateLinkedDoc = () => {
    setSelectedResource({
      oenumDoc: oenumdoc as string,
      linkType: '',
      codeType: '',
      protocol: '',
      typeDocSb: '',
      reference: '',
      revision: '',
      item: '',
    })
    setModalFormDisplayMode(DisplayModus.CREATE)
    setIsModalFormOpen(true)
  }

  const loadData = async () => {
    if (oenumdoc) {
      const response = await fetchLinkedDocs({
        lang: lang,
        oenumdoc: oenumdoc,
      })
      setData(response?.data?.content)
    }
  }

  /**
   * Load linked-documents of Work-order
   */
  useEffect(() => {
    if (isExpanded) {
      loadData()
    } else {
      setData([])
    }
  }, [lang, isExpanded])

  /**
   * Group by data (required resources) by type (keep ordering as it is)
   */
  useEffect(() => {
    if (data) {
      const dataGroupByType = {}
      const isExpandedGroupByType = {}
      data.forEach((objResource) => {
        const libType =
          Object.prototype.hasOwnProperty.call(objResource, 'libType') && objResource.libType !== ''
            ? objResource['libType']
            : EMPTY_DOC_CATEGORY
        if (Object.prototype.hasOwnProperty.call(dataGroupByType, libType)) {
          dataGroupByType[libType].push(objResource)
        } else {
          dataGroupByType[libType] = [objResource]
          isExpandedGroupByType[libType] = isExpanded
        }
      })
      setDataGroupedByType(dataGroupByType)
      setIsExpandedGroupedByType(isExpandedGroupByType)
    } else {
      setDataGroupedByType({})
      setIsExpandedGroupedByType({})
    }
  }, [data])

  /**
   * ExpandAll and collapseAll are mirror-toggled, so a hook on expandAll is enough
   */
  useEffect(() => {
    setIsExpanded(expandAll)

    if (JSON.stringify(isExpandedGroupedByType) !== '{}') {
      const copyIsExpandedGroupedByType = { ...isExpandedGroupedByType }
      Object.keys(copyIsExpandedGroupedByType).map((it) => (copyIsExpandedGroupedByType[it] = expandAll))
      setIsExpandedGroupedByType(copyIsExpandedGroupedByType)
    }
  }, [expandAll])

  const handleSubSectionToggleExpandCollapse = (evt: any) => {
    const actionName = evt.currentTarget.attributes['name'].nodeValue
    let copyIsExpandedGroupedByType = { ...isExpandedGroupedByType }
    copyIsExpandedGroupedByType[actionName] = !isExpandedGroupedByType[actionName]
    setIsExpandedGroupedByType(copyIsExpandedGroupedByType)
  }

  /**
   * Generate the URL of document and show the document up.
   * @param {*} e
   * @param {*} objLinkedDoc
   */
  const handleViewDocument = (e: any, objLinkedDoc: SelectedLinkedDoc) => {
    const rootUrlCleaned = tools.cleanUrl('/' + urlApis.URL_WORKORDER_DISPLAY_DOC_GET_PARAMETERIZED)
    const urlDocument = tools.dformat(
      rootUrlCleaned,
      oenumdoc,
      objLinkedDoc.codeType,
      objLinkedDoc.typeDocSb,
      objLinkedDoc.protocol,
      objLinkedDoc.reference,
      objLinkedDoc.revision,
      objLinkedDoc.item,
    )

    console.debug('Document url to display=' + urlDocument)
    setDocumentReference((objLinkedDoc.libType ? objLinkedDoc.libType : t('9500193')) + ' - ' + oenumdoc)
    setUrlViewDocument(urlDocument)
    setModalViewDocumentOpened(true)
  }

  const handleCloseModalViewDocument = () => {
    setModalViewDocumentOpened(false)
  }

  return (
    <div style={{ width: '100%' }}>
      <Collapsible title={t('1M00128')} expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
        <div>
          {canCreate && (
            <IconButton variant='ghost' size='small' onClick={handleCreateLinkedDoc} style={{ marginBottom: '1em' }}>
              <FontAwesomeIcon icon={faSquarePlus} />
            </IconButton>
          )}
        </div>
        <div>
          {JSON.stringify(dataGroupedByType) !== '{}' && JSON.stringify(dataGroupedByType) !== '[]' ? (
            <div>
              {Object.keys(dataGroupedByType).map((linkedDocType, i) => (
                <Fieldset
                  legend={
                    <>
                      {isExpandedGroupedByType[linkedDocType] ? (
                        <FontAwesomeIcon
                          name={linkedDocType}
                          icon={faChevronUp}
                          onClick={handleSubSectionToggleExpandCollapse}
                        />
                      ) : (
                        <FontAwesomeIcon
                          name={linkedDocType}
                          icon={faChevronDown}
                          onClick={handleSubSectionToggleExpandCollapse}
                        />
                      )}
                      {linkedDocType}
                    </>
                  }
                  key={'linkedDocType_' + i}
                >
                  <div
                    style={{
                      display: isExpandedGroupedByType[linkedDocType] ? 'inline-block' : 'none',
                    }}
                  >
                    <Table>
                      {[DOCUMENT_TYPES.CARD, DOCUMENT_TYPES.OPERATION].includes(
                        dataGroupedByType[linkedDocType][0]?.codeType,
                      ) ? (
                        <>
                          <colgroup>
                            <col style={{ width: '4%' }} />
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '35%' }} />
                            <col style={{ width: '4%' }} />
                          </colgroup>
                          <TableHead>
                            <TableRow>
                              <TableCell colSpan={2}>{t('1P00005')}</TableCell>
                              <TableCell>{t('9500042')}</TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                        </>
                      ) : dataGroupedByType[linkedDocType][0]?.codeType === DOCUMENT_TYPES.SB_AD_CN ? (
                        <>
                          <colgroup>
                            <col style={{ width: '4%' }} />
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '35%' }} />
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '4%' }} />
                          </colgroup>
                          <TableHead>
                            <TableRow>
                              <TableCell colSpan={2} style={{ width: '8em' }}>
                                {t('1P00012')}
                              </TableCell>
                              <TableCell>{t('9500042')}</TableCell>
                              <TableCell>{t('1LEX050')}</TableCell>
                              <TableCell>{t('0MRM050')}</TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                        </>
                      ) : (
                        <>
                          <colgroup>
                            <col style={{ width: '4%' }} />
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '35%' }} />
                            <col style={{ width: '4%' }} />
                          </colgroup>
                          <TableHead>
                            <TableRow>
                              <TableCell colSpan={2}>{t('1P00012')}</TableCell>
                              <TableCell>{t('1P00005')}</TableCell>
                              <TableCell>{t('9500042')}</TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                        </>
                      )}
                      <TableBody>
                        {dataGroupedByType[linkedDocType].map((objResource, j) =>
                          [DOCUMENT_TYPES.CARD, DOCUMENT_TYPES.OPERATION].includes(
                            dataGroupedByType[linkedDocType][0]?.codeType,
                          ) ? (
                            <TableRow key={'LinkedDocTypeRow_' + i + '_' + j}>
                              <TableCell style={{ width: '1em' }} align='center'>
                                {objResource.graphicDoc ? (
                                  <FontAwesomeIcon
                                    icon={faEye}
                                    className='color_primary'
                                    onClick={(e) => handleViewDocument(e, objResource)}
                                  />
                                ) : (
                                  ''
                                )}
                              </TableCell>
                              <TableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'protocol') &&
                                objResource?.protocol !== ''
                                  ? objResource['protocol']
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'reference') &&
                                objResource?.reference !== ''
                                  ? objResource['reference']
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {canUpdate && (
                                  <Inline>
                                    <IconButton
                                      disabled={objResource.linkType !== '00'}
                                      size='small'
                                      variant='ghost'
                                      onClick={() => setLinkedDocsToDelete({ ...objResource, open: true })}
                                    >
                                      {objResource?.protocol === linkedDocsToDelete?.protocol &&
                                      objResource?.reference === linkedDocsToDelete?.reference &&
                                      linkedDocsToDelete?.loading ? (
                                        <Spinner size='small' />
                                      ) : (
                                        <FontAwesomeIcon size='xs' icon={faTrash} />
                                      )}
                                    </IconButton>
                                  </Inline>
                                )}
                              </TableCell>
                            </TableRow>
                          ) : dataGroupedByType[linkedDocType][0]?.codeType === DOCUMENT_TYPES.SB_AD_CN ? (
                            <TableRow key={'LinkedDocTypeRow_' + i + '_' + j}>
                              <TableCell style={{ width: '1em' }} align='center'>
                                {objResource.graphicDoc ? (
                                  <FontAwesomeIcon
                                    icon={faEye}
                                    className='color_primary'
                                    onClick={(e) => handleViewDocument(e, objResource)}
                                  />
                                ) : (
                                  ''
                                )}
                              </TableCell>
                              <TableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'typeDocSb') &&
                                objResource?.typeDocSb !== ''
                                  ? objResource['typeDocSb']
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'reference') &&
                                objResource?.reference !== ''
                                  ? objResource['reference']
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'revision') &&
                                objResource?.revision !== ''
                                  ? objResource['revision']
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'item') && objResource?.item !== ''
                                  ? objResource['item']
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {canUpdate && (
                                  <Inline>
                                    <IconButton
                                      disabled={objResource.linkType !== '00'}
                                      size='small'
                                      variant='ghost'
                                      onClick={() => setLinkedDocsToDelete({ ...objResource, open: true })}
                                    >
                                      {objResource?.typeDocSb === linkedDocsToDelete?.typeDocSb &&
                                      objResource?.reference === linkedDocsToDelete?.reference &&
                                      objResource?.revision === linkedDocsToDelete?.revision &&
                                      objResource?.item === linkedDocsToDelete?.item &&
                                      linkedDocsToDelete?.loading ? (
                                        <Spinner size={13} />
                                      ) : (
                                        <FontAwesomeIcon size='xs' icon={faTrash} />
                                      )}
                                    </IconButton>
                                  </Inline>
                                )}
                              </TableCell>
                            </TableRow>
                          ) : (
                            <TableRow key={'LinkedDocTypeRow_' + i + '_' + j}>
                              <TableCell style={{ width: '1em' }} align='center'>
                                {objResource.graphicDoc ? (
                                  <FontAwesomeIcon
                                    icon={faEye}
                                    className='color_primary'
                                    onClick={(e) => handleViewDocument(e, objResource)}
                                  />
                                ) : (
                                  ''
                                )}
                              </TableCell>
                              <TableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'typeDocSb') &&
                                objResource?.typeDocSb !== ''
                                  ? objResource['typeDocSb']
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'protocol') &&
                                objResource?.protocol !== ''
                                  ? objResource['protocol']
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'reference') &&
                                objResource?.reference !== ''
                                  ? objResource['reference']
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {canUpdate && (
                                  <Inline>
                                    <IconButton
                                      disabled={objResource.linkType !== '00'}
                                      size='small'
                                      variant='ghost'
                                      onClick={() => setLinkedDocsToDelete({ ...objResource, open: true })}
                                    >
                                      {objResource?.typeDocSb === linkedDocsToDelete?.typeDocSb &&
                                      objResource?.reference === linkedDocsToDelete?.reference &&
                                      objResource?.protocol === linkedDocsToDelete?.protocol &&
                                      linkedDocsToDelete?.loading ? (
                                        <Spinner size={13} />
                                      ) : (
                                        <FontAwesomeIcon size='xs' icon={faTrash} />
                                      )}
                                    </IconButton>
                                  </Inline>
                                )}
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </Fieldset>
              ))}
            </div>
          ) : (
            <div style={{ paddingLeft: '1em' }}>{t('9500055')}</div>
          )}
        </div>
      </Collapsible>

      <ModalViewDocument
        title={t('1M00128')}
        documentReference={documentReference}
        urlDocument={urlViewDocument}
        isOpened={modalViewDocumentOpened}
        onCloseModal={handleCloseModalViewDocument}
      />

      <ModalConfirm
        isOpened={Boolean(linkedDocsToDelete?.open)}
        onCloseModal={() => setLinkedDocsToDelete(null)}
        confirmMessage={t('9510089')}
        onSubmit={handleDelete}
      />

      <ModalLinkedDoc
        lang={lang}
        open={isModalFormOpen}
        onClose={() => setIsModalFormOpen(false)}
        linkedDocument={selectedResource}
        onPostSubmit={handleModalFormPostSubmit}
      />
    </div>
  )
}

export default WoLinkedDocs
